import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Grid, List, ChevronLeft, ChevronRight, ArrowUp, ArrowDown } from 'lucide-react';
import ContactGroupCard from './ContactGroupCard';
import ContactGroupTableItem from './ContactGroupTableItem';
import ContactEmptyState from './ContactEmptyState';

const ContactGroupList = ({ 
  groups, 
  loading, 
  pagination,
  onPageChange,
  onPerPageChange,
  onViewGroup, 
  onDownloadGroup, 
  onDeleteGroup,
  onAddToGroup,
  onRefreshGroup,
}) => {
  const [viewMode, setViewMode] = useState('table');

  // Use backend pagination data
  const currentPage = pagination?.currentPage || 1;
  const totalPages = pagination?.lastPage || 1;
  const itemsPerPage = pagination?.perPage || 15;
  const totalItems = pagination?.total || 0;

  if (loading) {
    return viewMode === 'card' ? (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 sm:gap-6">
        {[...Array(8)].map((_, index) => (
          <div
            key={index}
            className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm animate-pulse sm:rounded-2xl sm:p-6"
          >
            <div className="flex gap-3 items-center mb-4">
              <div className="w-10 h-10 bg-gray-200 rounded-lg sm:w-12 sm:h-12"></div>
              <div className="flex-1">
                <div className="mb-2 w-3/4 h-4 bg-gray-200 rounded sm:h-5"></div>
                <div className="w-1/2 h-3 bg-gray-200 rounded sm:h-4"></div>
              </div>
            </div>
            <div className="flex justify-between items-center mb-4">
              <div className="w-1/3 h-4 bg-gray-200 rounded"></div>
              <div className="w-16 h-6 bg-gray-200 rounded-full sm:h-7"></div>
            </div>
            <div className="h-8 bg-gray-200 rounded-lg sm:h-10"></div>
          </div>
        ))}
      </div>
    ) : (
      <div className="overflow-x-auto border border-gray-200 rounded-xl shadow-sm">
        <table className="w-full min-w-[800px]">
          <thead>
            <tr className="bg-gray-50">
              <th className="py-3 px-4 w-[35%]"><div className="w-32 h-4 bg-gray-200 rounded"></div></th>
              <th className="py-3 px-4 w-[20%]"><div className="w-24 h-4 bg-gray-200 rounded"></div></th>
              <th className="py-3 px-4 w-[15%]"><div className="w-20 h-4 bg-gray-200 rounded"></div></th>
              <th className="py-3 px-4 w-[30%]"><div className="w-40 h-4 bg-gray-200 rounded"></div></th>
            </tr>
          </thead>
          <tbody>
            {[...Array(8)].map((_, index) => (
              <tr key={index} className="border-b border-gray-100 animate-pulse">
                <td className="py-4 px-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                    <div className="flex-1">
                      <div className="mb-1 w-32 h-4 bg-gray-200 rounded"></div>
                      <div className="w-24 h-3 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <div className="w-24 h-6 bg-gray-200 rounded-full"></div>
                </td>
                <td className="py-4 px-4">
                  <div className="w-16 h-4 bg-gray-200 rounded"></div>
                </td>
                <td className="py-4 px-4">
                  <div className="flex gap-2">
                    <div className="w-16 h-8 bg-gray-200 rounded-lg"></div>
                    <div className="w-16 h-8 bg-gray-200 rounded-lg"></div>
                    <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                    <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (!loading && groups.length === 0) {
    return <ContactEmptyState />;
  }

  // Use groups directly since backend handles pagination
  const currentItems = groups;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Header with View Toggle and Pagination Info */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        {/* Results count */}
        <div className="text-sm text-gray-600">
          Showing <span className="font-semibold">{pagination?.from || 0}</span> to{' '}
          <span className="font-semibold">{pagination?.to || 0}</span> of{' '}
          <span className="font-semibold">{totalItems}</span> groups
        </div>

        {/* View Toggle and Pagination Controls */}
        <div className="flex items-center gap-4">
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <motion.button
                onClick={() => onPageChange && onPageChange(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg border ${
                  currentPage === 1
                    ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'text-gray-600 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                }`}
                whileTap={{ scale: 0.95 }}
              >
                <ChevronLeft className="w-4 h-4" />
              </motion.button>
              
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <motion.button
                      key={pageNum}
                      onClick={() => onPageChange && onPageChange(pageNum)}
                      className={`px-3 py-1.5 text-sm rounded-lg ${
                        currentPage === pageNum
                          ? 'bg-blue-50 text-blue-600 font-semibold border border-blue-100'
                          : 'text-gray-600 hover:bg-gray-50 border border-transparent'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {pageNum}
                    </motion.button>
                  );
                })}
                
                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <>
                    <span className="px-1 text-gray-400">...</span>
                    <motion.button
                      onClick={() => onPageChange && onPageChange(totalPages)}
                      className={`px-3 py-1.5 text-sm rounded-lg ${
                        currentPage === totalPages
                          ? 'bg-blue-50 text-blue-600 font-semibold border border-blue-100'
                          : 'text-gray-600 hover:bg-gray-50 border border-transparent'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {totalPages}
                    </motion.button>
                  </>
                )}
              </div>
              
              <motion.button
                onClick={() => onPageChange && onPageChange(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-lg border ${
                  currentPage === totalPages
                    ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'text-gray-600 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                }`}
                whileTap={{ scale: 0.95 }}
              >
                <ChevronRight className="w-4 h-4" />
              </motion.button>
            </div>
          )}

          {/* View Toggle Buttons */}
          <div className="inline-flex items-center bg-white rounded-lg border border-gray-200 p-1 shadow-sm">
            <motion.button
              onClick={() => setViewMode('card')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                viewMode === 'card'
                  ? 'bg-blue-50 text-blue-600 border border-blue-100'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              whileTap={{ scale: 0.95 }}
            >
              <Grid className="w-4 h-4" />
              <span className="hidden sm:inline">Cards</span>
            </motion.button>
            <motion.button
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                viewMode === 'table'
                  ? 'bg-blue-50 text-blue-600 border border-blue-100'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              whileTap={{ scale: 0.95 }}
            >
              <List className="w-4 h-4" />
              <span className="hidden sm:inline">Table</span>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Content based on view mode */}
      {viewMode === 'card' ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 sm:gap-6">
          {currentItems.map((group) => (
            <ContactGroupCard
              key={group.id || group.group_name}
              group={group}
              onView={onViewGroup}
              onDownload={onDownloadGroup}
              onDelete={onDeleteGroup}
              onAddToGroup={onAddToGroup}
              onRefreshGroup={onRefreshGroup}
            />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-xl shadow-sm bg-white">
          <table className="w-full table-fixed min-w-[800px]">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 w-[35%]">Group Name</th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 w-[20%]">Contacts</th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 w-[15%]">Status</th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 w-[30%]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((group) => (
                <ContactGroupTableItem
                  key={group.id || group.group_name}
                  group={group}
                  onView={onViewGroup}
                  onDownload={onDownloadGroup}
                  onDelete={onDeleteGroup}
                  onAddToGroup={onAddToGroup}
                  onRefreshGroup={onRefreshGroup}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Bottom Pagination */}
      {/* {groups.length > itemsPerPage && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Page <span className="font-semibold">{currentPage}</span> of{' '}
            <span className="font-semibold">{totalPages}</span> •{' '}
            <span className="font-semibold">{groups.length}</span> total groups
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Items per page:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                const newItemsPerPage = parseInt(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="6">6</option>
              <option value="12">12</option>
              <option value="24">24</option>
              <option value="48">48</option>
            </select>
          </div>
        </div>
      )} */}

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        {/* Results count */}
        <div className="text-sm text-gray-600">
          Showing <span className="font-semibold">{pagination?.from || 0}</span> to{' '}
          <span className="font-semibold">{pagination?.to || 0}</span> of{' '}
          <span className="font-semibold">{totalItems}</span> groups
        </div>

        {/* View Toggle and Pagination Controls */}
        <div className="flex items-center gap-4">
          {/* Items per page selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Per page:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => onPerPageChange && onPerPageChange(parseInt(e.target.value))}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <motion.button
                onClick={() => onPageChange && onPageChange(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg border ${
                  currentPage === 1
                    ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'text-gray-600 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                }`}
                whileTap={{ scale: 0.95 }}
              >
                <ChevronLeft className="w-4 h-4" />
              </motion.button>
              
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <motion.button
                      key={pageNum}
                      onClick={() => onPageChange && onPageChange(pageNum)}
                      className={`px-3 py-1.5 text-sm rounded-lg ${
                        currentPage === pageNum
                          ? 'bg-blue-50 text-blue-600 font-semibold border border-blue-100'
                          : 'text-gray-600 hover:bg-gray-50 border border-transparent'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {pageNum}
                    </motion.button>
                  );
                })}
                
                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <>
                    <span className="px-1 text-gray-400">...</span>
                    <motion.button
                      onClick={() => onPageChange && onPageChange(totalPages)}
                      className={`px-3 py-1.5 text-sm rounded-lg ${
                        currentPage === totalPages
                          ? 'bg-blue-50 text-blue-600 font-semibold border border-blue-100'
                          : 'text-gray-600 hover:bg-gray-50 border border-transparent'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {totalPages}
                    </motion.button>
                  </>
                )}
              </div>
              
              <motion.button
                onClick={() => onPageChange && onPageChange(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-lg border ${
                  currentPage === totalPages
                    ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'text-gray-600 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                }`}
                whileTap={{ scale: 0.95 }}
              >
                <ChevronRight className="w-4 h-4" />
              </motion.button>
            </div>
          )}

          {/* View Toggle Buttons */}
          <div className="inline-flex items-center bg-white rounded-lg border border-gray-200 p-1 shadow-sm">
            <motion.button
              onClick={() => setViewMode('card')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                viewMode === 'card'
                  ? 'bg-blue-50 text-blue-600 border border-blue-100'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              whileTap={{ scale: 0.95 }}
            >
              <Grid className="w-4 h-4" />
              <span className="hidden sm:inline">Cards</span>
            </motion.button>
            <motion.button
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                viewMode === 'table'
                  ? 'bg-blue-50 text-blue-600 border border-blue-100'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              whileTap={{ scale: 0.95 }}
            >
              <List className="w-4 h-4" />
              <span className="hidden sm:inline">Table</span>
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ContactGroupList;